// src/components/JarvislyModule/PageHeader/RefreshButton.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React from 'react';
import { Button, Tooltip } from 'antd';
import translateX from 'utils/translateX';
import { useModuleStore } from 'stores/module';
import { ReloadOutlined } from '@ant-design/icons';
import { useDocumentContext } from 'components/core/DocumentProvider';
import { useListContext } from 'components/core/ListProvider';
import useZustand from 'hooks/useZustand';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
export const RefreshButton = ({
  actions = [],
  actionContext,
  label,
  tooltip,
  icon,
  type,
  disabled,
  onClick,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedCurrentPage = useZustand('module', 'currentPage');
  const apiGlobal = useZustand('global', 'useApi');
  const { viewMode: mode, setViewMode: setMode } = useDocumentContext();

  // Get refresh directly from ListContext for reactive updates
  // (Using listContext from ModuleContext would have stale closure values)
  const { refresh: listRefresh } = useListContext() || {};

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Check for actions - support both string and object formats
  const hasAction = key =>
    actions.includes(key) || actions.some(a => typeof a === 'object' && a?.action === key);

  const showRefreshButton = hasAction('refresh') || (hasAction('editRefresh') && mode !== 'view');

  // Support disabled as function (receives { page, moduleContext })
  const resolvedDisabled =
    typeof disabled === 'function' ? disabled({ page: storedCurrentPage }) : disabled;

  const computedDisabled =
    resolvedDisabled ?? (storedCurrentPage?.api?.returnAsObject && !storedCurrentPage?.data?._id);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleClick = () => {
    // If a custom onClick override is provided in the action config, use it exclusively
    if (typeof onClick === 'function') {
      onClick();
      return;
    }

    // For list pages, use the refresh function directly from ListContext
    if (typeof listRefresh === 'function') {
      listRefresh();
      return;
    }

    // For list pages without ListContext access, use global refresh with proper params
    if (storedCurrentPage?.layout === 'list') {
      const apiUrl = storedCurrentPage?.api?.url;
      const apiQuery = storedCurrentPage?.api?.query;
      apiGlobal?.refresh?.({ url: apiUrl, query: apiQuery, showLoading: true, hideLoading: true });
      return;
    }

    // For document pages, use the global API refresh
    const _id = storedCurrentPage?.data?._id;
    apiGlobal?.refresh?.({ _id, showLoading: true, hideLoading: true });
    setMode?.('view');
  };

  // Prevent blur from stealing focus before click registers
  const handleMouseDown = e => {
    e.preventDefault();
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const buttonIcon = icon || <ReloadOutlined />;
  const buttonType = type;
  const title = translateX(tooltip || label || 'refresh');

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!showRefreshButton) return null;

  return (
    <Tooltip title={title} placement="top">
      <Button
        type={buttonType}
        // style={computedDisabled ? { backgroundColor: '#ffffff' } : undefined}
        disabled={computedDisabled}
        icon={buttonIcon}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
      >
        {isLabelProvided(label) ? translateX(label) : null}
      </Button>
    </Tooltip>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default RefreshButton;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function isLabelProvided(label) {
  return !!label && typeof label === 'string';
}
